import { AxiosResponse } from 'axios';
import _toUpper from 'lodash/toUpper';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  RbacBundledRolesResponseType,
  RbacCustomRolesResponseType,
} from 'src/app/interfaces/IUserRoles';
import { getCombinedRoles } from 'src/app/utils/shared';
import {
  ClickAway,
  CloseErrorBanners,
  FetchAllGroups,
  FetchAllProductsInstances,
  FetchAllRoles,
  FetchAllUsers,
  FetchFeatureFlags,
} from './actions';
import {
  filterMultipleInstanceProducts,
  filterNonSystemGroups,
} from './helpers';
import { getAuth, getFeatureFlagsSet, getNavigation } from './selectors';
import { phanesAPIClient, prometheusAPIClient } from '../../api/clients';
import { IDataProps } from '../../config/interfaces';
import { getConfigData } from '../../config/selectors';
import { IActionWithPayload } from '../../core/redux/interfaces';
import {
  IFetchAllProductsInstances,
  IFetchGroupsResponseType,
  IGroup,
  IUser,
} from '../../interfaces/ICommon';
import { notifyFormPopulated } from '../../services/formPopulated';
import {
  FEATURE_FLAG_CONSTANTS,
  FEATURE_FLAG_PREFIX,
  SECURITY_CLOUD_CONTROL_READ_ONLY,
} from '../../utils/constant';
import { getEnterpriseId } from '../enterprises/selectors';
import {
  ClearEditGroup,
  ClearMapGroupsStepper,
  CloseErrorBanners as CloseGroupsErrorBanner,
  CloseMapGroupsErrorBanner,
  DiscardAddGroupChanges,
  HideDiscardChangesModal,
  ShowDiscardChangesModal,
} from '../groups/actions';
import { getIsAddGroupDirty } from '../groups/selectors';
import {
  ClearEditingUserRole,
  ClearRolesTableConfigData,
  ClearSelectedUserRoles,
  DiscardCustomRoleStepper,
  HideUserRoleNotification,
  ToggleDiscardChangesModal as ToggleDiscardCustomRoleModal,
} from '../user-roles/actions';
import { getIsCustomRoleDirty } from '../user-roles/selectors';
import {
  ClearUsers,
  DiscardInviteChanges,
  ToggleDiscardChangesModal as ToggleDiscardUserDetailsModal,
} from '../users/actions';
import { RbacRolesResponseType } from '../users/interfaces';
import { populateRolesSelect } from '../users/populateRolesSelect';
import { getIsUserDetailsDirty } from '../users/selectors';

type TFetchAllProductsInstancesResp =
  | AxiosResponse<{ data: IFetchAllProductsInstances }>
  | any;

export function* handleClickAway(
  action: IActionWithPayload<{ origin: string; shouldRedirect: boolean }>
): Generator {
  const {
    payload: { shouldRedirect = true },
  } = action;
  const { signOutCallbackFn } = (yield select(getAuth)) as any;
  const { navigateFn, redirectPath, pathname } = (yield select(
    getNavigation
  )) as any;

  const isGroupsActive = pathname.includes('groups');
  const isEditGroupActive = pathname.includes('edit-group');
  const isUsersActive =
    pathname.includes('users') && !pathname.includes('edit-user');
  const isUserRolesActive =
    pathname.includes('user-roles') || pathname.includes('edit-user-role');

  const isAddGroupDirty = (yield select(getIsAddGroupDirty)) as boolean;
  const isUserDetailsDirty = (yield select(getIsUserDetailsDirty)) as boolean;
  const isCustomRoleDirty = (yield select(getIsCustomRoleDirty)) as boolean;
  if (isGroupsActive && isAddGroupDirty) {
    yield put(ShowDiscardChangesModal());
    return;
  }

  if (isUsersActive && isUserDetailsDirty) {
    yield put(ToggleDiscardUserDetailsModal(true));
    return;
  }

  if (isUserRolesActive && isCustomRoleDirty) {
    yield put(ToggleDiscardCustomRoleModal(true));
    return;
  }

  if (signOutCallbackFn) {
    yield call(signOutCallbackFn);
    return;
  }

  if (shouldRedirect) {
    if (isGroupsActive && !isAddGroupDirty) {
      yield put(ClearMapGroupsStepper());
    }
    if (isGroupsActive) {
      yield put(CloseMapGroupsErrorBanner());
    }

    if (isUsersActive && !isUserDetailsDirty) {
      yield put(ClearUsers());
    }

    if (isUserRolesActive) {
      yield put(ClearRolesTableConfigData());
      yield put(ClearSelectedUserRoles());
      yield put(ClearEditingUserRole());
      yield put(HideUserRoleNotification());
    }

    if (isEditGroupActive) {
      yield put(ClearEditGroup());
    }

    yield call(navigateFn, redirectPath);
  }
}

export function* handleFetchFeatureFlags(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  try {
    if (!enterpriseId) {
      return;
    }

    const response = (yield prometheusAPIClient.get<string[]>(
      `/api/featureFlag/${enterpriseId}`
    )) as AxiosResponse<string[], any>;
    yield put(FetchFeatureFlags.SUCCESS(response.data));
  } catch (error) {
    yield put(FetchFeatureFlags.ERROR(error as Error));
  }
}

export function* handleDiscardChanges(
  action: IActionWithPayload<{ shouldRedirect: boolean }>
): Generator {
  const { payload: { shouldRedirect = true } = {} } = action;

  const { redirectPath, navigateFn, pathname } = (yield select(
    getNavigation
  )) as any;
  const { signOutCallbackFn } = (yield select(getAuth)) as any;

  yield call(notifyFormPopulated, { isDirty: false });

  if (pathname.includes('groups')) {
    yield put(HideDiscardChangesModal());
  }

  if (pathname.includes('users')) {
    yield put(ToggleDiscardUserDetailsModal(false));
  }

  if (pathname.includes('user-roles')) {
    yield put(ToggleDiscardCustomRoleModal(false));
  }

  if (signOutCallbackFn) {
    yield call(signOutCallbackFn);
    return;
  }

  if (shouldRedirect) {
    yield call(navigateFn, redirectPath);
  }
}

export function* handleFetchAllProductsInstances(): Generator {
  const enterpriseId = yield select(getEnterpriseId);
  const config = yield select(getConfigData);

  try {
    const env = (config as IDataProps)?.env;
    const response: TFetchAllProductsInstancesResp = yield phanesAPIClient.get(
      `v1/enterprise/${enterpriseId}/products`
    );

    // TODO: remove this condition once CN envs supports single-tenant policy
    if (
      env &&
      ['cn-ci', 'cn-preview', 'cn-staging', 'cn-prod'].includes(
        env.toLowerCase()
      )
    ) {
      yield put(
        FetchAllProductsInstances.SUCCESS(response.data.productInstancesInfo)
      );
    } else {
      const { productInstancesInfo, multipleProductTypes } =
        filterMultipleInstanceProducts(response.data.productInstancesInfo);
      localStorage.setItem(
        'MultipleProductTypes',
        JSON.stringify(multipleProductTypes)
      );

      yield put(FetchAllProductsInstances.SUCCESS(productInstancesInfo));
    }
  } catch (error) {
    yield put(FetchAllProductsInstances.ERROR(error as Error));
  }
}

export function* handleFetchAllUsers(): Generator {
  const enterpriseId = yield select(getEnterpriseId);
  try {
    const response: IUser[] | any = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/users`
    );
    yield put(FetchAllUsers.SUCCESS(response.data.items));
  } catch (error) {
    yield put(FetchAllUsers.ERROR(error as Error));
  }
}

export function* handleCloseErrorBanners(): Generator {
  yield put(CloseGroupsErrorBanner());
}

export function* handleFetchAllGroups(): Generator {
  const enterpriseId = (yield select(getEnterpriseId)) as string;
  const featureFlagsSet = (yield select(getFeatureFlagsSet)) as Set<string>;
  try {
    const response = (yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/groups`
    )) as AxiosResponse<IFetchGroupsResponseType>;
    const rbacGroups: IGroup[] = response.data.rbacGroups;
    const hasSystemGroupFeatureFlag = featureFlagsSet.has(
      FEATURE_FLAG_CONSTANTS.IAM_SYSTEM_GROUPS_10194
    );
    const filteredGroups = filterNonSystemGroups(rbacGroups);
    yield put(
      FetchAllGroups.SUCCESS(
        hasSystemGroupFeatureFlag ? rbacGroups : filteredGroups
      )
    );
  } catch (error) {
    yield put(FetchAllGroups.ERROR(error as Error));
  }
}

export function* handleFetchAllRoles(): Generator {
  const enterpriseId = yield select(getEnterpriseId);

  yield call(handleFetchFeatureFlags);
  const featureFlagsSet = (yield select(getFeatureFlagsSet)) as Set<string>;

  try {
    const response: RbacRolesResponseType = yield prometheusAPIClient.get(
      `/api/enterprises/${enterpriseId}/rbac/roles`
    );
    const customRolesResponse: RbacCustomRolesResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/customRoles`
      );
    const bundledRolesResponse: RbacBundledRolesResponseType =
      yield prometheusAPIClient.get(
        `/api/enterprises/${enterpriseId}/rbac/bundledRoles`
      );

    const rbacRoles = getCombinedRoles(
      response,
      customRolesResponse,
      featureFlagsSet.has(FEATURE_FLAG_CONSTANTS.BUNDLED_ROLE)
        ? bundledRolesResponse
        : null
    ).filter((role) =>
      new Set(
        role.featureFlags?.map((flag) =>
          _toUpper(flag).replace(FEATURE_FLAG_PREFIX, '')
        )
      ).isSubsetOf(featureFlagsSet)
    );

    const rolesWithFilteredReadOnly = featureFlagsSet.has(
      FEATURE_FLAG_CONSTANTS.SCC_READ_ONLY
    )
      ? rbacRoles
      : rbacRoles.filter(
          (role) => role.roleDisplayName !== SECURITY_CLOUD_CONTROL_READ_ONLY
        );

    yield put(FetchAllRoles.SUCCESS(rolesWithFilteredReadOnly));
    yield populateRolesSelect();
  } catch (error) {
    yield put(FetchAllRoles.ERROR(error as Error));
  }
}

export function* commonSaga(): Generator {
  yield takeEvery(ClickAway.TYPE, handleClickAway);
  yield takeEvery(
    [
      DiscardInviteChanges.TYPE,
      DiscardAddGroupChanges.TYPE,
      DiscardCustomRoleStepper.TYPE,
    ],
    handleDiscardChanges
  );
  yield takeLatest(
    FetchAllProductsInstances.Pending.TYPE,
    handleFetchAllProductsInstances
  );
  yield takeLatest(FetchAllUsers.Pending.TYPE, handleFetchAllUsers);
  yield takeLatest(FetchAllRoles.Pending.TYPE, handleFetchAllRoles);
  yield takeLatest(FetchFeatureFlags.Pending.TYPE, handleFetchFeatureFlags);
  yield takeLatest(CloseErrorBanners.TYPE, handleCloseErrorBanners);
  yield takeLatest(FetchAllGroups.Pending.TYPE, handleFetchAllGroups);
}
